import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import api from "../api/client";
import ConsoleInvitePanel from "../components/ConsoleInvitePanel";
import { Device } from "../types";

type Status = "creating" | "connecting" | "active" | "closed" | "error";

export default function DeviceShell() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [status, setStatus] = useState<Status>("creating");
  const [error, setError] = useState<string>("");
  const [device, setDevice] = useState<Device | null>(null);
  /* The session token, in state ONLY so a colleague can be invited to watch.
   * The socket itself uses the local inside the effect - lifting it here for
   * the effect too would tie a live connection to a re-render. */
  const [sessionToken, setSessionToken] = useState("");

  // Initialize xterm + WS exactly once for this device id. The complex
  // lifecycle here is deliberate: we want a single fresh terminal per
  // navigation, not re-init across React re-renders.
  useEffect(() => {
    let cancelled = false;
    let term: Terminal | null = null;
    let ws: WebSocket | null = null;

    // Send resize control message as a TEXT frame carrying JSON. Server's
    // shell broker preserves frame type all the way to the agent, which
    // recognises text frames as control. Binary frames remain raw I/O.
    const sendResize = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const t = termRef.current;
      if (!t) return;
      try {
        wsRef.current.send(
          JSON.stringify({
            type: "resize",
            cols: t.cols,
            rows: t.rows,
          }),
        );
      } catch {
        /* ignore */
      }
    };

    // Defer fit() to after layout + paint. xterm's Viewport reads
    // dimensions during fit() that may be undefined if the renderer
    // hasn't finished mounting yet — that's the
    // "Cannot read properties of undefined (reading 'dimensions')"
    // crash. rAF schedules the call after the next paint by which
    // time the flex parent has measured the container.
    const safeFit = () => {
      const el = containerRef.current;
      const fit = fitRef.current;
      if (!fit || !el || el.clientWidth === 0 || el.clientHeight === 0) return;
      try {
        fit.fit();
      } catch {
        requestAnimationFrame(safeFit);
      }
    };
    const safeWriteln = (line: string) => {
      const t = termRef.current;
      if (!t) return;
      try {
        t.writeln(line);
      } catch {
        // If the renderer isn't ready yet, defer one frame.
        requestAnimationFrame(() => {
          try {
            t.writeln(line);
          } catch {
            /* give up — message is non-essential UI chrome */
          }
        });
      }
    };

    const setup = async () => {
      // 1. Load device metadata (for the header)
      try {
        const r = await api.get(`/api/v1/devices/${id}`);
        if (!cancelled) setDevice(r.data);
      } catch {
        /* non-fatal */
      }

      // 2. Create the terminal even before the WS opens so we can render
      // status messages into it.
      term = new Terminal({
        convertEol: true,
        cursorBlink: true,
        fontFamily: 'Consolas, "Cascadia Mono", "JetBrains Mono", "Liberation Mono", monospace',
        fontSize: 14,
        theme: { background: "#0a0e1a", foreground: "#e6edf3" },
        scrollback: 5000,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      termRef.current = term;
      fitRef.current = fit;
      if (containerRef.current) {
        term.open(containerRef.current);
        // Two rAF ticks: one for layout, one for xterm renderer init.
        requestAnimationFrame(() => requestAnimationFrame(safeFit));
      }
      safeWriteln("\x1b[90m[aerogauge] Requesting shell session…\x1b[0m");

      // 3. Ask the server to create a session + queue an open_shell command
      let token: string;
      try {
        const r = await api.post(`/api/v1/shell/sessions`, { device_id: Number(id) });
        token = r.data.token;
        setSessionToken(token);
      } catch (err: any) {
        if (cancelled) return;
        const msg = err.response?.data?.detail || err.message;
        safeWriteln(`\x1b[31m[aerogauge] Failed to create session: ${msg}\x1b[0m`);
        setError(msg);
        setStatus("error");
        return;
      }
      if (cancelled) return;
      safeWriteln(
        `\x1b[90m[aerogauge] Session token: ${token.slice(0, 8)}… waiting for agent\x1b[0m`,
      );
      setStatus("connecting");

      // 4. Open the WebSocket. The server-side endpoint waits up to 120s
      // for the agent to attach; meanwhile our WS stays open showing
      // "waiting".
      const jwt = localStorage.getItem("token") || "";
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${wsProto}//${window.location.host}/api/v1/shell/ws/${token}?access_token=${encodeURIComponent(jwt)}`;
      ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("active");
        term?.writeln("\x1b[32m[aerogauge] Connected — start typing.\x1b[0m");
        term?.focus();
        // Send initial dimensions so the agent's PTY matches what xterm
        // is rendering. Without this the PTY stays at its default
        // 30x120 and PowerShell will wrap weirdly.
        sendResize();
      };

      ws.onmessage = (evt) => {
        if (!term) return;
        if (evt.data instanceof ArrayBuffer) {
          term.write(new Uint8Array(evt.data));
        } else if (typeof evt.data === "string") {
          term.write(evt.data);
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        term?.writeln("\r\n\x1b[31m[aerogauge] WebSocket error.\x1b[0m");
        setStatus("error");
      };

      ws.onclose = (evt) => {
        if (cancelled) return;
        const reason = evt.reason || `code ${evt.code}`;
        term?.writeln(`\r\n\x1b[90m[aerogauge] Disconnected (${reason}).\x1b[0m`);
        setStatus("closed");
      };

      // Wire user keystrokes → WS. xterm gives us strings (UTF-8); we send
      // as TextEncoder bytes so server + agent always see consistent binary.
      const enc = new TextEncoder();
      term.onData((data) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(enc.encode(data));
        }
      });
    };

    setup();

    // Resize handler: re-fit the local renderer, then tell the agent
    // so its PTY dimensions match. Debounced so a drag-resize doesn't
    // spam the WS.
    let resizeTimer: number | undefined;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        safeFit();
        sendResize();
      }, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      try {
        term?.dispose();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
      termRef.current = null;
      fitRef.current = null;
    };
  }, [id]);

  const close = () => {
    try {
      wsRef.current?.close();
    } catch {
      /* ignore */
    }
    navigate(`/devices/${id}`);
  };

  const statusBadge = (() => {
    const map: Record<Status, { label: string; cls: string }> = {
      creating: { label: "Creating session…", cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
      connecting: { label: "Waiting for agent…", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" },
      active: { label: "Connected", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
      closed: { label: "Disconnected", cls: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
      error: { label: "Error", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
    };
    const s = map[status];
    return <span className={`text-xs px-2 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>;
  })();

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Remote shell — {device?.hostname || `device #${id}`}
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Every keystroke is audit-logged. Type{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">exit</code> to close.
          </div>
        </div>
        <div className="flex items-center gap-3">
          {statusBadge}
          {/* Without this the shell watcher is unreachable: the invite panel
              only ever existed on the screen-share toolbar, so there was no
              way to invite anyone to a SHELL session even though the server
              and the broker have supported it all along. */}
          {sessionToken && status === "active" && (
            <ConsoleInvitePanel kind="shell" sessionToken={sessionToken} compact />
          )}
          <button
            onClick={close}
            className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40"
          >
            Close
          </button>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 mb-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 rounded-xl shadow-sm overflow-x-auto"
        style={{ backgroundColor: "#0a0e1a", minHeight: "60vh", padding: "10px" }}
      />
    </div>
  );
}
